import styled from "styled-components";

import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { StatusTone } from "@pages/AdminDashboard/ui/StatusBadge/statusTone";
import { ToneBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { MedicationInclusion, OrderMedication, OrderMedicationsState } from "@types-runway/admin";

const { bannerTone, color, radius } = adminTokens;
const success = bannerTone.success;
const [, spaceSm, spaceMd, , spaceLg] = adminTokens.spacing;

const Box = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  overflow: hidden;
`;

// An included medication's row takes the Success fill (MEDS-R14).
const Row = styled.li<{ $isIncluded: boolean }>`
  display: flex;
  background-color: ${({ $isIncluded }) => ($isIncluded ? success.background : color.surface)};
  align-items: center;
  justify-content: space-between;
  gap: ${spaceSm}px ${spaceLg}px;
  padding: ${spaceMd - 2}px ${spaceLg - 2}px;

  & + & {
    border-top: 1px solid ${color.border};
  }
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Name = styled.em`
  font-family: ${adminFontFamily.mediumItalic};
  font-style: italic;
`;

// On the Success fill the soft ink falls under 4.5:1, so the directions take the Success ink
// (MEDS-R16).
const Directions = styled.span<{ $isIncluded: boolean }>`
  color: ${({ $isIncluded }) => ($isIncluded ? success.text : color.inkSoft)};
`;

const IncludedMark = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${spaceSm}px;
  color: ${success.text};
`;

const IncludedLabel = styled.span`
  color: ${success.text};
  font-family: ${adminFontFamily.medium};
`;

const Status = styled.span`
  color: ${color.inkSoft};
`;

// Each mark's words and look (MEDS-R2…R5).
const marks: Record<MedicationInclusion, { label: string; tone: StatusTone }> = {
  included: { label: "Included", tone: "done" },
  not_included: { label: "Not included", tone: "inactive" },
  not_sent: { label: "Not sent yet", tone: "needsAttention" },
  unknown: { label: "Unknown", tone: "inactive" },
};

// "Included" is text beside a check mark, never told by the row's fill alone (MEDS-R15).
const Included = () => (
  <IncludedMark>
    <svg
      data-testid="medication-included-check"
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m5 12 5 5 9-10" />
    </svg>
    <IncludedLabel>{marks.included.label}</IncludedLabel>
  </IncludedMark>
);

const Medication = ({ medication }: { medication: OrderMedication }) => {
  const mark = marks[medication.inclusion];
  const isIncluded = medication.inclusion === "included";

  return (
    <Row $isIncluded={isIncluded} aria-label={`${medication.name}, ${mark.label}`}>
      <Text>
        <Name>{medication.name}</Name>
        <Directions $isIncluded={isIncluded}>{medication.directions}</Directions>
      </Text>
      {isIncluded ? <Included /> : <ToneBadge tone={mark.tone}>{mark.label}</ToneBadge>}
    </Row>
  );
};

interface MedicationListProps {
  // Absent until the Order's medications are asked for.
  medications?: OrderMedicationsState;
}

// An Order's medications, each with its name, its directions and what the Order sent
// (MEDS-R1…R7).
export const MedicationList = ({ medications }: MedicationListProps) => {
  if (!medications || medications.status === "pending") {
    return <Status>Loading medications…</Status>;
  }
  if (medications.status === "failed") {
    return <Status>The medications couldn&apos;t be loaded.</Status>;
  }
  if (medications.medications.length === 0) {
    return <Status>No medications on this Order.</Status>;
  }

  return (
    <Box>
      {medications.medications.map((medication) => (
        <Medication key={medication.caseOfferingId} medication={medication} />
      ))}
    </Box>
  );
};
